import { map, trim } from "lodash";
import React from "react";
import PropTypes from "prop-types";
import Tooltip from "@/components/Tooltip";
import EditTagsDialog from "./EditTagsDialog";
import PlainButton from "@/components/plain-button/PlainButton";
import "./TagsControl.less";
const tagColors = {
  alert: "#ccff00",
  analytics: "#ebdee6",
  arbitrum: "#00a3ff",
  avalanche: "#e84142",
  balancer: "#5b5efd",
  bad: "#fa8072 ",
  binance: "#f3ba2f",
  bsc: "#f0b90b",
  convex: "#37d0fd",
  crvusd: "#ffe699",
  curve: "#ffe699",
  dao: "#cbaacb",
  defaultColor: "#d3d3d3",
  discord: "#7289DA",
  dola: "#d5e7b8",
  dbr: "#a7d489",
  dune: "#F1603F",
  ethereum: "#627eea",
  fantom: "#1969ff",
  fed: "#c4dea4",
  firm: "#627eea",
  good: "#1ED760",
  graph: "#90ffca",
  hack: "#fa8072 ",
  inverse: "#cbaacb",
  "inverse-watch": "#cbaacb",
  "inverse watch": "#cbaacb",
  "inverse-stats": "#cbaacb",
  inv: "#cbaacb",
  "lp balances": "#cbaacb",
  "Michael Egorov": "#37d0fd",
  monthly: "#cbaacb",
  optimistic: "#ff0402",
  polygon: "#8247e5",
  risk: "#ffaea5",
  rwg: "#ffaea5",
  sdola: "#c4dea4",
  sinv: "#cbaacb",
  subgraph: "#90ffca",
  treasury: "#ffffb5",
  twg: "#ffffb5",
  twitter: "#1DA1F2",
  velodrome: "#00cfcc",
  watch: "#cbaacb",
  weekly: "#cbaacb",
  zerion: "#1A117A",
};


export class TagsControl extends React.Component {
  static propTypes = {
    tags: PropTypes.arrayOf(PropTypes.string),
    canEdit: PropTypes.bool,
    getAvailableTags: PropTypes.func,
    onEdit: PropTypes.func,
    className: PropTypes.string,
    tagsExtra: PropTypes.node,
    tagSeparator: PropTypes.node,
    children: PropTypes.node,
  };

  static defaultProps = {
    tags: [],
    canEdit: false,
    getAvailableTags: () => Promise.resolve([]),
    onEdit: () => {},
    className: "",
    tagsExtra: null,
    tagSeparator: null,
    children: null,
  };

  editTags = (tags, getAvailableTags) => {
    EditTagsDialog.showModal({ tags, getAvailableTags }).onClose(this.props.onEdit);
  };

  getTagColor(tag) {
    // Find the matching color by converting the keys to lowercase for comparison
    const tagColorKey = Object.keys(tagColors).find(
      (key) => key.toLowerCase() === tag.toLowerCase() || tag.toLowerCase().includes(key.toLowerCase())
    );
  
    // Return the matching color or fallback to 'defaultColor'
    return tagColors[tagColorKey] || tagColors["defaultColor"];
  }


  renderEditButton() {
    const tags = map(this.props.tags, trim);
    return (
      <PlainButton
        className="label label-tag hidden-xs"
        onClick={() => this.editTags(tags, this.props.getAvailableTags)}
        data-test="EditTagsButton">
        {tags.length === 0 && (
          <React.Fragment>
            <i className="zmdi zmdi-plus m-r-5" aria-hidden="true" />
            Add tag
          </React.Fragment>
        )}
        {tags.length > 0 && (
          <>
            <i className="zmdi zmdi-edit" aria-hidden="true" />
            <span className="sr-only">Edit</span>
          </>
        )}
      </PlainButton>
    );
  }

  render() {
    const { tags, tagSeparator } = this.props;
    return (
      <div className={"tags-control " + this.props.className} data-test="TagsControl">
        {this.props.children}
        {map(tags, (tag, i) => (
          <React.Fragment key={tag}>
            {tagSeparator && i > 0 && <span className="tag-separator">{tagSeparator}</span>}
            <span
              className="label label-tag"
              key={tag}
              title={tag}
              data-test="TagLabel"
              style={{ backgroundColor: this.getTagColor(tag) }}>
              {tag}
            </span>
          </React.Fragment>
        ))}
        {this.props.canEdit && this.renderEditButton()}
        {this.props.tagsExtra}
      </div>
    );
  }
}

function modelTagsControl({ archivedTooltip }) {
  function ModelTagsControl({ isDraft, isArchived, ...props }) {
    return (
      <TagsControl {...props}>
        {!isArchived && isDraft && <span className="label label-tag-unpublished">Unpublished</span>}
        {isArchived && (
          <Tooltip placement="right" title={archivedTooltip}>
            <span className="label label-tag-archived">Archived</span>
          </Tooltip>
        )}
      </TagsControl>
    );
  }

  ModelTagsControl.propTypes = {
    isDraft: PropTypes.bool,
    isArchived: PropTypes.bool,
  };

  ModelTagsControl.defaultProps = {
    isDraft: false,
    isArchived: false,
  };

  return ModelTagsControl;
}

function modelTagsControle({ archivedTooltip }) {
  function ModelTagsControle({ isArchived, ...props }) {
    return (
      <TagsControl {...props}>
        {isArchived && (
          <Tooltip placement="right" title={archivedTooltip}>
            <span className="label label-tag-archived">Archived</span>
          </Tooltip>
        )}
      </TagsControl>
    );
  }

  ModelTagsControle.propTypes = {
    isArchived: PropTypes.bool,
  };

  ModelTagsControle.defaultProps = {
    isArchived: false,
  };

  return ModelTagsControle;
}

export const QueryTagsControl = modelTagsControl({
  archivedTooltip: "This query is archived and can't be used in dashboards, or appear in search results.",
});

export const DashboardTagsControl = modelTagsControl({
  archivedTooltip: "This dashboard is archived and won't be listed in dashboards nor search results.",
});

export const AlertTagsControl = modelTagsControle({
  archivedTooltip: "This alert is archived and won't be listed in alerts nor search results.",
});

export const AlertEventTagsControl = modelTagsControle({
  archivedTooltip: "This event is archived and won't be listed in alerts nor search results.",
});

export const MLModelTagsControl = modelTagsControle({
  archivedTooltip: "This model is archived and won't be listed in alerts nor search results.",
});

export const PredictionsTagsControl = modelTagsControle({
  archivedTooltip: "This prediction is archived and won't be listed in alerts nor search results.",
});

export const MLModelsVersionsTagsControl = modelTagsControle({
  archivedTooltip: "This version is archived and won't be listed in alerts nor search results.",
});

export const IndexerTagsControl = modelTagsControle({
  archivedTooltip: "This indexer is archived and won't be listed in indexers nor search results.",
});